class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);



class HeaderMenu extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;
    this.header = document.querySelector('.header-wrapper');
    this.details = this.querySelector('details');
    this.detailsBg = this.header.querySelector('.mega-menu-bg');
    this.content = this.details.querySelector('.mega-menu__wrapper');
    this.stickyHeader = this.closest('sticky-header');
    this.ANIMATION_DURATION = 250; // 0.25s in milliseconds
    this.menuList = this.header.querySelector('.header__inline-menu');

    this.detailsBg.addEventListener('mousedown', this.doNothing);
    this.content.addEventListener('mousedown', this.doNothing);
    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));

    /**
     * Browser find-in-page (Cmd/Ctrl+F) auto-expands a closed <details> that contains a match,
     * without moving focus into it. focusout then never fires, so the menu also needs to close
     * on a click outside or on Escape.
     */
    document.addEventListener('pointerdown', this.onDocumentPointerDown.bind(this));
    document.addEventListener('keydown', this.onDocumentKeyDown.bind(this));

    /**
     * Special scroll-to-section behavior for index page when hero is in view.
     * Requirements: When a menu item is clicked while the hero image is visible on the index page,
     * instead of immediately opening the menu, first smooth scroll to whichever is closer:
     * - The top of the hero section (containing .cmp-hero)
     * - The top of the next section after the hero
     * After scrolling completes, then open the menu as normal.
     * If the menu is already open, skip this behavior and let normal menu actions proceed.
     */
    this.addEventListener("click", (e) => {
      if (document.body.classList.contains('component--hero-image--in-view') && document.body.classList.contains('template--index')) {
        // If nav is already open, let normal menu behavior handle this
        if (this.mainDetailsToggle.hasAttribute('open')) {
          return;
        }
        
        e.preventDefault();
        e.stopPropagation();
        
        // Get current scroll position
        const currentScrollY = window.scrollY;
        
        // Find .cmp-hero element
        const heroElement = document.querySelector('.cmp-hero');
        
        // Find the section containing the hero
        let heroSection = null;
        if (heroElement) {
          heroSection = heroElement.closest('section');
        }
        
        // Find the next section after the hero section
        let nextSection = null;
        if (heroSection) {
          // Look for the next sibling section
          let current = heroSection.nextElementSibling;
          while (current) {
            if (current.nodeType === Node.ELEMENT_NODE && current.tagName.toLowerCase() === 'section') {
              nextSection = current;
              break;
            }
            current = current.nextElementSibling;
          }
        }
        
        let targetScrollY = 0;
        
        if (heroSection && nextSection) {
          // Calculate absolute positions from top of page
          const heroTop = heroSection.offsetTop;
          const nextSectionTop = nextSection.offsetTop;
          
          // Calculate distances to scroll each to the top of viewport
          const distanceToHero = Math.abs(currentScrollY - heroTop);
          const distanceToNext = Math.abs(currentScrollY - nextSectionTop);
          
          // Choose the closer target
          targetScrollY = distanceToHero <= distanceToNext ? heroTop : nextSectionTop;
        } else if (heroSection) {
          // Only hero section exists, scroll to it
          targetScrollY = heroSection.offsetTop;
        } else if (nextSection) {
          // Only next section exists, scroll to it
          targetScrollY = nextSection.offsetTop;
        }
        
        // Smooth scroll to target
        window.scrollTo({
          top: targetScrollY,
          behavior: 'smooth'
        });
        
        // Wait for scroll to complete, then open the menu
        const checkScrollComplete = () => {
          if (Math.abs(window.scrollY - targetScrollY) < 5) {
            // Scroll is complete, now open the menu
            if (!this.mainDetailsToggle.hasAttribute('open')) {
              this.mainDetailsToggle.setAttribute('open', '');
              this.onToggle();
            }
          } else {
            requestAnimationFrame(checkScrollComplete);
          }
        };
        
        requestAnimationFrame(checkScrollComplete);
        return;
      }
    })
  }

  doNothing(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  onFocusOut(event) {
    const isClickInside = event.relatedTarget !== null && (event.relatedTarget?.closest('header-menu') !== null || event.relatedTarget?.closest('.mega-menu-bg') !== null);

    if (!isClickInside) {
      this.closeMenu();
    }
  }

  onDocumentPointerDown(event) {
    if (!this.details.open) return;
    if (event.target.closest('header-menu') || event.target.closest('.mega-menu-bg')) return;

    this.closeMenu();
  }

  onDocumentKeyDown(event) {
    if (event.key !== 'Escape' || !this.details.open) return;

    this.closeMenu();
  }

  onToggle(e) {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.openMenu();
    } else {
      this.closeMenu();
    }
  }

  openMenu() {
    // Add submenu-open class immediately
    this.menuList.classList.add('submenu-open');
    document.body.classList.add('mega-menu-open');

    // Close all other menus first
    const headerMenus = document.querySelectorAll('header-menu');
    headerMenus.forEach(menu => {
      if(menu !== this && menu.details.open) {
        menu.details.open = false;
      }
    });

    this.content.style.maxWidth = this.detailsBg.offsetWidth - this.content.getBoundingClientRect().x + 'px';

    // Ensure content is hidden before animation
    this.content.style.visibility = 'hidden';
    this.content.style.opacity = '0';

    this.details.open = true;

    // Use requestAnimationFrame to ensure the initial state is applied
    requestAnimationFrame(() => {
      // Make content visible again
      this.content.style.visibility = 'visible';
      
      this.content.animate([
        { opacity: 0 },
        { opacity: 1 }
      ], {
        duration: this.ANIMATION_DURATION,
        easing: 'ease-out',
        fill: 'forwards'
      });
    });
  }

  closeMenu() {
    const animation = this.content.animate([
      { opacity: 1 },
      { opacity: 0 }
    ], {
      duration: this.ANIMATION_DURATION,
      easing: 'ease-in',
      fill: 'forwards'
    });

    animation.onfinish = () => {
      this.details.open = false;
      
      // Check if any menu is still open
      const isAnyMenuOpen = Array.from(document.querySelectorAll('header-menu')).some(menu => menu.details.open);
      if (!isAnyMenuOpen) {
        this.menuList.classList.remove('submenu-open');
        document.body.classList.remove('mega-menu-open');
      }
    };
  }
}

customElements.define('header-menu', HeaderMenu);


